import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, Calendar, Video as VideoIcon, BookOpen, Sparkles, ChevronDown, ChevronUp, Loader, FileText, Gem, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';
import {
  getQuestionHistory,
  downloadQuestionHistory,
  downloadQuestionTranscript,
  getPracticeProblemsForQuestion,
  submitPracticeSolution,
  renderVideoFromSolution,
  getVideoStatus,
  generateSimilarProblems,
  getCreditStatus,
  getPixelSessions,
  deletePixelSession,
} from '../services/api';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import PracticeProblemModal from '../components/PracticeProblemModal';
import './QuestionHistory.css';

const normaliseLatex = (text) => {
  if (!text) return text;
  return text
    .replace(/\\\[/g, '$$').replace(/\\\]/g, '$$')
    .replace(/\\\(/g, '$').replace(/\\\)/g, '$');
};

const QuestionHistory = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = translations[language]?.questionHistory || {};
  const modalTranslations = translations[language]?.problemSolver || {};
  
  const [activeTab, setActiveTab] = useState('history'); // 'history' | 'sessions'
  const [pixelSessions, setPixelSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedQuestions, setExpandedQuestions] = useState(new Set());
  const [filterSubject, setFilterSubject] = useState('all');
  const [filterDays, setFilterDays] = useState(30);
  
  // Practice problems carousel
  const [practiceProblems, setPracticeProblems] = useState([]);
  const [currentProblemIndex, setCurrentProblemIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loadingPractice, setLoadingPractice] = useState(false);
  const [currentQuestionId, setCurrentQuestionId] = useState(null);
  
  // Video generation state
  const [generatingVideoFor, setGeneratingVideoFor] = useState(null);
  const [videoPollingIntervals, setVideoPollingIntervals] = useState({});
  
  // Practice test generation state
  const [generatingPracticeFor, setGeneratingPracticeFor] = useState(null);

  // Credit cost constant
  const COST_RENDER_VIDEO = 2;

  // Refresh credit status after operations
  const refreshCreditStatus = useCallback(async () => {
    try {
      await getCreditStatus();
      window.dispatchEvent(new CustomEvent('creditStatusChanged'));
      console.log('💎 Credit status refreshed and event dispatched');
    } catch (err) {
      console.error('Error refreshing credit status:', err);
    }
  }, []);
  
  // Use useCallback to memoize loadHistory function
  const loadHistory = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getQuestionHistory({
        subject: filterSubject === 'all' ? null : filterSubject,
        days: filterDays,
        limit: 50,
        offset: 0
      });
      
      console.log('📚 History loaded:', data.questions?.length, 'items');
      
      if (data.questions && data.questions.length > 0) {
        const firstItem = data.questions[0];
        console.log('First history item:', {
          id: firstItem.id,
          solution_id: firstItem.solution_id,
          video_created: firstItem.video_created,
          practice_problems_count: firstItem.practice_problems_count,
          has_solution: !!firstItem.ai_solution,
          has_image: !!firstItem.question_image_url,
          topics: firstItem.topics
        });
      }
      
      setHistory(data.questions || []);
      
      // Check for any in-progress video generation
      if (data.questions && data.questions.length > 0) {
        data.questions.forEach(item => {
          // If video is not created but solution exists, check if video is being generated
          if (!item.video_created && item.solution_id) {
            // Check video status
            getVideoStatus(item.solution_id).then(status => {
              console.log(`🎬 Checking video status for question ${item.id}:`, status);
              
              if (status.status === 'creating' || status.status === 'rendering' || status.status === 'queued') {
                // Video is still being generated, resume polling
                console.log(`🔄 Resuming video polling for question ${item.id}`);
                setGeneratingVideoFor(item.id);
                startVideoPolling(item.id, item.solution_id);
              } else if (status.status === 'completed' && status.video_url) {
                // Video is complete but not reflected in history, update it
                console.log(`✅ Video completed for question ${item.id}, updating history`);
                setHistory(prev => prev.map(historyItem => 
                  historyItem.id === item.id 
                    ? { ...historyItem, video_created: true, video_url: status.video_url, thumbnail_url: status.thumbnail_url }
                    : historyItem
                ));
              }
            }).catch(err => {
              // Video status not found or error, ignore
              console.log(`ℹ️ No video status found for question ${item.id}`);
            });
          }
        });
      }
    } catch (error) {
      console.error('❌ Error loading history:', error);
    } finally {
      setIsLoading(false);
    }
  }, [filterSubject, filterDays]);
  
  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const loadPixelSessions = useCallback(async () => {
    setSessionsLoading(true);
    try {
      const data = await getPixelSessions();
      setPixelSessions(data || []);
    } catch (err) {
      console.error('Failed to load pixel sessions:', err);
    } finally {
      setSessionsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'sessions') loadPixelSessions();
  }, [activeTab, loadPixelSessions]);

  const handleDeleteSession = async (sessionId) => {
    try {
      await deletePixelSession(sessionId);
      setPixelSessions(prev => prev.filter(s => s.id !== sessionId));
    } catch (err) {
      console.error('Failed to delete session:', err);
    }
  };

  const handleResumeSession = (sessionId) => {
    navigate('/pixel-ai', { state: { sessionId } });
  };

  // Cleanup polling intervals on unmount
  useEffect(() => {
    return () => {
      Object.values(videoPollingIntervals).forEach(interval => {
        if (interval) clearInterval(interval);
      });
    };
  }, [videoPollingIntervals]);
  
  const toggleQuestion = (questionId) => {
    const newExpanded = new Set(expandedQuestions);
    if (newExpanded.has(questionId)) {
      newExpanded.delete(questionId);
    } else {
      newExpanded.add(questionId);
    }
    setExpandedQuestions(newExpanded);
  };
  
  const handleDownloadAll = async () => {
    try {
      const blob = await downloadQuestionHistory('all', {
        subject: filterSubject === 'all' ? null : filterSubject,
        days: filterDays
      });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'question-history.pdf';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading history:', error);
      alert('Failed to download history. Please try again.');
    }
  };

  const handleDownloadSingle = async (questionId) => {
    try {
      const blob = await downloadQuestionTranscript(questionId);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `question-${questionId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading question:', error);
      alert('Failed to download question. Please try again.');
    }
  };
  
  const handleViewPracticeProblems = async (questionId) => {
    setLoadingPractice(true);
    setCurrentQuestionId(questionId);
    try {
      const response = await getPracticeProblemsForQuestion(questionId);
      setPracticeProblems(response.problems || []);
      setCurrentProblemIndex(0);
      setIsModalOpen(true);
    } catch (error) {
      console.error('Error loading practice problems:', error);
      alert('Failed to load practice problems. Please try again.');
    } finally {
      setLoadingPractice(false);
    }
  };
  
  const handleGeneratePractice = async (item) => {
    if (!item.solution_id) {
      alert('No solution ID available. Please try re-solving the problem.');
      return;
    }
    
    setGeneratingPracticeFor(item.id);
    
    try {
      console.log('🎯 Generating practice problems for solution:', item.solution_id);
      
      // ✅ FIXED: Only pass solution_id and count
      // Language is automatically taken from the original solution in the database
      const response = await generateSimilarProblems(
        item.solution_id,
        3  // count - generates 3 practice problems
        // difficulty_levels will use default ['easy', 'medium', 'hard']
      );
      
      console.log('✅ Practice problems generated:', response);
      
      // Reload history to show updated practice status
      await loadHistory();
      
    } catch (error) {
      console.error('❌ Error generating practice problems:', error);
      console.error('❌ Error response:', error.response?.data);  // Shows 422 details
      alert('Failed to generate practice problems. Please try again.');
    } finally {
      setGeneratingPracticeFor(null);
    }
  };
  
  const handleModalClose = () => {
    setIsModalOpen(false);
    setPracticeProblems([]);
    setCurrentProblemIndex(0);
    setCurrentQuestionId(null);
  };
  
  const handleNextProblem = () => {
    if (currentProblemIndex < practiceProblems.length - 1) {
      setCurrentProblemIndex(currentProblemIndex + 1);
    }
  };
  
  const handlePreviousProblem = () => {
    if (currentProblemIndex > 0) {
      setCurrentProblemIndex(currentProblemIndex - 1);
    }
  };
  
  const handlePracticeSolutionSubmit = async (data) => {
    try {
      await submitPracticeSolution({
        problem_id: data.problemId,
        user_solution: data.userSolution,
        is_correct: data.isCorrect,
        time_seconds: data.timeSpent
      });
      
      console.log('✅ Practice solution submitted');
      
      // Auto-advance to next problem or close
      setTimeout(() => {
        if (currentProblemIndex < practiceProblems.length - 1) {
          setCurrentProblemIndex(currentProblemIndex + 1);
        } else {
          handleModalClose();
        }
      }, 2000);
      
    } catch (error) {
      console.error('Error submitting practice solution:', error);
    }
  };
  
  const handleGenerateVideo = async (item) => {
    if (!item.solution_id) {
      console.error('No solution_id found for this question');
      alert('No solution ID available for this question. Please try re-solving the problem.');
      return;
    }
    
    setGeneratingVideoFor(item.id);
    
    try {
      // Use the item's language if available, otherwise fall back to current UI language
      const videoLanguage = item.language || language;
      console.log('🎬 Creating video for solution:', item.solution_id, 'in language:', videoLanguage);
      
      const response = await renderVideoFromSolution(
        item.solution_id, 
        'ql', 
        '16:9',
        videoLanguage
      );
      console.log('✅ Video generation started successfully:', response);
      
      // Refresh credit status after creating video
      await refreshCreditStatus();
      
      // Start polling for video status
      const videoId = response.video_id || response.id || item.solution_id;
      console.log('📊 Polling video ID:', videoId);
      startVideoPolling(item.id, videoId);
      
    } catch (error) {
      console.error('❌ Error generating video:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      setGeneratingVideoFor(null);
      
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      alert(`Failed to start video generation: ${errorMessage}\n\nPlease try again or contact support if the issue persists.`);
    }
  };
  
  const startVideoPolling = (questionId, videoId) => {
    const interval = setInterval(async () => {
      try {
        const status = await getVideoStatus(videoId);
        console.log(`📊 Video status for question ${questionId}:`, status);
        
        if (status.status === 'completed' && status.video_url) {
          // Update history to reflect new video
          setHistory(prev => prev.map(item => 
            item.id === questionId 
              ? { ...item, video_created: true, video_url: status.video_url, thumbnail_url: status.thumbnail_url }
              : item
          ));
          
          // Clear interval
          clearInterval(interval);
          setVideoPollingIntervals(prev => {
            const newIntervals = { ...prev };
            delete newIntervals[questionId];
            return newIntervals;
          });
          setGeneratingVideoFor(null);
          
        } else if (status.status === 'failed') {
          console.error('Video generation failed');
          clearInterval(interval);
          setVideoPollingIntervals(prev => {
            const newIntervals = { ...prev };
            delete newIntervals[questionId];
            return newIntervals;
          });
          setGeneratingVideoFor(null);
          alert('Video generation failed. Please try again.');
        }
      } catch (error) {
        console.error('Error polling video status:', error);
      }
    }, 3000);
    
    setVideoPollingIntervals(prev => ({
      ...prev,
      [questionId]: interval
    }));
  };
  
  const handleSolveAgain = (item) => {
    navigate('/dashboard', {
      state: {
        question: item.question_text,
        subject: item.subject
      }
    });
  };
  
  const getSubjectEmoji = (subject) => {
    const emojis = {
      chemistry: '🧪',
      physics: '⚛️',
      mathematics: '📐'
    };
    return emojis[subject] || '📚';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="question-history-page">
      <div className="question-history-container">

        {/* Tab bar */}
        <div className="history-tabs">
          <div className="history-tabs-left">
            <button
              className={`history-tab ${activeTab === 'history' ? 'active' : ''}`}
              onClick={() => setActiveTab('history')}
            >
              📚 {t.title || 'Question History'}
            </button>
            <button
              className={`history-tab ${activeTab === 'sessions' ? 'active' : ''}`}
              onClick={() => setActiveTab('sessions')}
            >
              💬 {t.chatSessions || 'Chat Sessions'}
            </button>
          </div>
          {activeTab === 'history' && history.length > 0 && (
            <button onClick={handleDownloadAll} className="download-all-btn">
              <Download size={15} />
              <span>{t.downloadAll || 'Download All'}</span>
            </button>
          )}
        </div>

        {/* Chat Sessions Tab */}
        {activeTab === 'sessions' && (
          <div className="sessions-list">
            {sessionsLoading ? (
              <div className="loading-state">
                <Loader className="spinner-large" size={40} />
                <p>{t.loadingSessions || 'Loading sessions...'}</p>
              </div>
            ) : pixelSessions.length === 0 ? (
              <div className="empty-state">
                <p>{t.noSessions || 'No chat sessions yet. Start a conversation in Pixel AI!'}</p>
                <button onClick={() => navigate('/pixel-ai')} className="start-chat-btn">
                  {t.openPixelAI || 'Open Pixel AI'}
                </button>
              </div>
            ) : (
              pixelSessions.map(session => (
                <div key={session.id} className="session-card">
                  <div className="session-info" onClick={() => handleResumeSession(session.id)}>
                    <div className="session-title">{session.title || t.untitledSession || 'Untitled session'}</div>
                    <div className="session-meta">
                      <span className="session-subject">{session.subject}</span>
                      {session.concept_title && (
                        <span className="session-concept">📖 {session.concept_title}</span>
                      )}
                      <span className="session-msgs">{session.message_count} {t.messagesCount || 'messages'}</span>
                      <span className="session-date">
                        {new Date(session.updated_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <div className="session-actions">
                    <button
                      onClick={() => handleResumeSession(session.id)}
                      className="session-resume-btn"
                    >
                      {t.resume || 'Resume'}
                    </button>
                    <button
                      onClick={() => handleDeleteSession(session.id)}
                      className="session-delete-btn"
                      title="Delete session"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Filters (only on history tab) */}
        {activeTab === 'history' && (
          <div className="history-filters">
            <div className="filter-group">
              <label>{t.subject || 'Subject'}:</label>
              <select
                value={filterSubject}
                onChange={(e) => setFilterSubject(e.target.value)}
                className="filter-select"
              >
                <option value="all">{t.allSubjects || 'All Subjects'}</option>
                <option value="chemistry">{t.chemistry || 'Chemistry'}</option>
                <option value="physics">{t.physics || 'Physics'}</option>
                <option value="mathematics">{t.mathematics || 'Mathematics'}</option>
              </select>
            </div>

            <div className="filter-group">
              <label>{t.timePeriod || 'Time Period'}:</label>
              <select
                value={filterDays}
                onChange={(e) => setFilterDays(Number(e.target.value))}
                className="filter-select"
              >
                <option value={7}>{t.last7Days || 'Last 7 Days'}</option>
                <option value={30}>{t.last30Days || 'Last 30 Days'}</option>
                <option value={90}>{t.last90Days || 'Last 90 Days'}</option>
                <option value={365}>{t.lastYear || 'Last Year'}</option>
              </select>
            </div>
          </div>
        )}

        {/* History tab content */}
        {activeTab === 'history' && <>

        {/* Loading State */}
        {isLoading && (
          <div className="loading-state">
            <Loader className="spinner-large" size={48} />
            <p>{t.loading || 'Loading questions...'}</p>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && history.length === 0 && (
          <div className="empty-state">
            <FileText size={64} color="#9ca3af" />
            <h3>{t.noQuestions || 'No Questions Yet'}</h3>
            <p>{t.noQuestionsDesc || 'Start solving problems to build your history'}</p>
            <button 
              onClick={() => navigate('/dashboard')} 
              className="start-solving-btn"
            >
              {t.startSolving || 'Start Solving'}
            </button>
          </div>
        )}

        {/* Questions List */}
        {!isLoading && history.length > 0 && (
          <div className="history-list">
            {history.map((item) => (
              <div key={item.id} className="history-item">
                <div 
                  className="history-item-header"
                  onClick={() => toggleQuestion(item.id)}
                >
                  <div className="history-item-info">
                    <div className="history-item-meta">
                      <span className={`subject-badge ${item.subject}`}>
                        <span>{getSubjectEmoji(item.subject)}</span>
                        <span>{item.subject}</span>
                      </span>
                      
                      <span className="history-date">
                        <Calendar size={14} />
                        {formatDate(item.created_at)}
                      </span>

                      {item.video_created && (
                        <span className="history-badge video-badge">
                          <VideoIcon size={12} />
                          {t.video || 'Video'}
                        </span>
                      )}

                      {item.practice_problems_count > 0 && (
                        <span className="history-badge practice-badge">
                          <Sparkles size={12} />
                          {item.practice_problems_count} {t.practice || 'Practice'}
                        </span>
                      )}
                    </div>

                    <p className="history-question-preview">
                      {item.question_text && item.question_text.length > 150
                        ? item.question_text.substring(0, 150) + '...'
                        : item.question_text || 'No question text available'}
                    </p>
                    
                    {/* Topics/Tags */}
                    {item.topics && item.topics.length > 0 && (
                      <div className="history-topics">
                        {item.topics.map((topic, idx) => (
                          <span key={idx} className="topic-tag-small">
                            {topic}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <button className="expand-btn">
                    {expandedQuestions.has(item.id) ? (
                      <ChevronUp size={20} />
                    ) : (
                      <ChevronDown size={20} />
                    )}
                  </button>
                </div>

                {expandedQuestions.has(item.id) && (
                  <div className="history-item-content">
                    {/* Video Player */}
                    {item.video_url && (
                      <div className="content-section">
                        <h4>🎬 {t.videoSolution || 'Video Solution'}</h4>
                        <div className="video-player-container">
                          <video
                            src={item.video_url}
                            controls
                            controlsList="nodownload"
                            className="video-player"
                            playsInline
                          >
                            Your browser does not support the video tag.
                          </video>
                        </div>
                      </div>
                    )}

                    {/* Full Question with Image */}
                    <div className="content-section">
                      <h4>❓ {t.question || 'Question'}</h4>
                      <div className="question-full">
                        <ReactMarkdown
                          remarkPlugins={[remarkMath]}
                          rehypePlugins={[rehypeKatex]}
                        >
                          {normaliseLatex(item.question_text || 'No question text available')}
                        </ReactMarkdown>
                        
                        {/* Question Image */}
                        {item.question_image_url && (
                          <div className="question-image-container">
                            <img 
                              src={item.question_image_url} 
                              alt="Question" 
                              className="question-image"
                              loading="lazy"
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* AI Solution */}
                    {item.ai_solution && (
                      <div className="content-section">
                        <h4>✨ {t.solution || 'Solution'}</h4>
                        <div className="solution-full">
                          <ReactMarkdown
                            remarkPlugins={[remarkMath]}
                            rehypePlugins={[rehypeKatex]}
                          >
                            {normaliseLatex(item.ai_solution)}
                          </ReactMarkdown>
                        </div>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="history-item-actions">
                      <button
                        onClick={() => handleSolveAgain(item)}
                        className="action-btn-small"
                      >
                        <BookOpen size={16} />
                        <span>{t.solveAgain || 'Solve Again'}</span>
                      </button>

                      <button
                        onClick={() => handleDownloadSingle(item.id)}
                        className="action-btn-small"
                      >
                        <Download size={16} />
                        <span>{t.downloadPDF || 'Download PDF'}</span>
                      </button>

                      {!item.video_created && generatingVideoFor !== item.id && (
                        <button
                          onClick={() => handleGenerateVideo(item)}
                          className="action-btn-small video-btn"
                        >
                          <VideoIcon size={16} />
                          <span>{t.generateVideo || 'Generate Video'}</span>
                          <span className="card-credit-badge">
                            -{COST_RENDER_VIDEO} <Gem size={14} className="gem-icon-card" />
                          </span>
                        </button>
                      )}

                      {generatingVideoFor === item.id && (
                        <button
                          disabled
                          className="action-btn-small video-btn"
                        >
                          <span className="spinner-tiny"></span>
                          <span>{t.generating || 'Generating...'}</span>
                        </button>
                      )}

                      {item.practice_problems_count > 0 && (
                        <button
                          onClick={() => handleViewPracticeProblems(item.id)}
                          disabled={loadingPractice && currentQuestionId === item.id}
                          className="action-btn-small practice-btn"
                        >
                          {loadingPractice && currentQuestionId === item.id ? (
                            <>
                              <span className="spinner-tiny"></span>
                              <span>{t.loading || 'Loading...'}</span>
                            </>
                          ) : (
                            <>
                              <BookOpen size={16} />
                              <span>{t.viewPractice || 'View Practice'} ({item.practice_problems_count})</span>
                            </>
                          )}
                        </button>
                      )}

                      {item.practice_problems_count === 0 && generatingPracticeFor !== item.id && (
                        <button
                          onClick={() => handleGeneratePractice(item)}
                          className="action-btn-small practice-generate-btn"
                        >
                          <Sparkles size={16} />
                          <span>{t.generatePractice || 'Generate Practice'}</span>
                        </button>
                      )}

                      {generatingPracticeFor === item.id && (
                        <button
                          disabled
                          className="action-btn-small practice-generate-btn"
                        >
                          <span className="spinner-tiny"></span>
                          <span>{t.generating || 'Generating...'}</span>
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        </>}
      </div>

      {/* Practice Problem Modal */}
      {isModalOpen && practiceProblems.length > 0 && (
        <PracticeProblemModal
          problem={practiceProblems[currentProblemIndex]}
          isOpen={isModalOpen}
          onClose={handleModalClose}
          onSubmit={handlePracticeSolutionSubmit}
          translations={modalTranslations}
          currentIndex={currentProblemIndex}
          totalProblems={practiceProblems.length}
          onNext={handleNextProblem}
          onPrevious={handlePreviousProblem}
          hasNext={currentProblemIndex < practiceProblems.length - 1}
          hasPrevious={currentProblemIndex > 0}
        />
      )}
    </div>
  );
};

export default QuestionHistory;